// Recipients tab. Paginated table; if the campaign is anonymized, shows the
// aggregate-only view (status buckets) per UX doc §13.5.

import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Typo, ListTable, Loading, Flex } from '../../../../ui';
import { useGetCampaignRecipientsQuery } from '../../campaignsApi';

const Wrap = styled.div`
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
`;

const AggregateGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px;
`;

const AggCard = styled.div`
  flex: 1 1 160px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: ${({ theme }) => theme.colors.NEU_50};
  border-radius: 8px;
`;

const AggLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const AggValue = styled.span`
  font-size: 22px;
  font-weight: 700;
  margin-top: 4px;
`;

const RecipientCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RecipientName = styled.span`
  font-weight: 500;
`;

const RecipientEmail = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
};

const RecipientsTab = ({ campaign }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetCampaignRecipientsQuery({ id: campaign.id, page: 1, pageSize: 200 });

  const tableInfo = useMemo(() => ([
    {
      label: t('campaigns.recipients.recipient', 'Recipient'),
      data: (item) => (
        <RecipientCell>
          <RecipientName>{item.name ?? '—'}</RecipientName>
          <RecipientEmail>{item.email}</RecipientEmail>
        </RecipientCell>
      ),
    },
    { label: t('campaigns.recipients.status', 'Status'), data: (item) => item.status },
    { label: t('campaigns.recipients.opened', 'Opened'), data: (item) => formatDate(item.openedAt) },
    { label: t('campaigns.recipients.clicked', 'Clicked'), data: (item) => formatDate(item.clickedAt) },
    {
      label: t('campaigns.recipients.revenue', 'Revenue'),
      data: (item) => (item.conversionValue ? `$${item.conversionValue.toLocaleString()}` : '—'),
    },
  ]), [t]);

  if (isLoading) {
    return <Flex justify='center' align='center' style={{ minHeight: 160 }}><Loading /></Flex>;
  }

  if (data?.anonymized) {
    const agg = data.aggregate ?? {};
    return (
      <Wrap>
        <div style={{ padding: 20 }}>
          <Typo.SmallText color='textSecondary'>
            {t('campaigns.recipients.anonymizedBanner', 'Recipient details were anonymized. Aggregate counts are preserved.')}
          </Typo.SmallText>
        </div>
        <AggregateGrid>
          {[
            ['recipients', t('campaigns.metric.recipients', 'Recipients')],
            ['delivered', t('campaigns.metric.delivered', 'Delivered')],
            ['opened', t('campaigns.metric.opened', 'Opened')],
            ['clicked', t('campaigns.metric.clicked', 'Clicked')],
            ['converted', t('campaigns.metric.converted', 'Converted')],
            ['bounces', t('campaigns.metric.bounces', 'Bounces')],
            ['unsubscribes', t('campaigns.metric.unsubscribes', 'Unsubscribes')],
          ].map(([key, label]) => (
            <AggCard key={key}>
              <AggLabel>{label}</AggLabel>
              <AggValue>{(agg[key] ?? 0).toLocaleString()}</AggValue>
            </AggCard>
          ))}
        </AggregateGrid>
      </Wrap>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <Wrap>
        <Typo.SmallText color='textSecondary' style={{ padding: 32, textAlign: 'center' }}>
          {t('campaigns.recipients.noDeliveries', 'No deliveries yet. First opens usually appear within 20 minutes.')}
        </Typo.SmallText>
      </Wrap>
    );
  }

  return (
    <ListTable
      tableName='campaign-recipients'
      data={items}
      tableInfo={tableInfo}
      defaultRowPerPage={25}
    />
  );
};

export default RecipientsTab;
